import { resolveRecruitmentPermissions } from "../../services/recruitmentPermissions";
import React, { useState } from "react";
import dayjs from "dayjs";
import Popup from "../../../DesignSystem/Popups/Popup";
import NewInputField from "../../../DesignSystem/Inputs/NewInputField";
import NewDateField from "../../../DesignSystem/Inputs/NewDateField";
import NewTextArea from "../../../DesignSystem/Inputs/NewTextArea";
import CheckboxMultiSelect from "../../../DesignSystem/Dropdowns/CheckboxMultiSelect";
import RadioGroup from "../../../DesignSystem/SelectionControls/RadioGroup";
import AdviserStatus from "../../../Adviser/Constants/Status";
import { formatDateValue } from "./Utils/dateFormatting";
import { saveSharedRecruitmentUpdate } from "../../services/recruitmentPipelineService";
import "../../../../css/Recruitment/RecruitmentUpdatePopup.css";

const UpdateRecruitmentStatus = ({
  isOpen,
  onClose,
  updateData,
  setUpdateData,
  advisorId,
  onSaved,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Helper: convert Date object or ISO string to ISO format for storage
  const dateToISO = (dateValue) => {
    if (!dateValue) return "";
    try {
      if (dateValue instanceof Date) {
        return dateValue.toISOString();
      }
      // If it's a string, try to parse it
      const parsed = dayjs(dateValue);
      return parsed.isValid() ? parsed.toISOString() : "";
    } catch (error) {
      // console.error("[dateToISO] Error converting date:", error);
      return "";
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (saving) return;
    setError("");

    try {
      if (!advisorId) {
        setError("Missing advisorId for this update.");
        return;
      }
      setSaving(true);

      const payload = {
        applicantName: (updateData?.applicantName ?? "").trim(),
        source: (updateData?.source ?? "").trim(),
        arTsName: (updateData?.arTsName ?? "").trim(),
        lastContacted: dateToISO(updateData?.lastContacted),
        appointmentData: (updateData?.appointmentData),
        description: (updateData?.description ?? "").trim(),
        lastUpdated: new Date().toISOString(),
        permission: selectedPermissions.join(", "),
        status: selectedPermissions,
        isAR: updateData?.isAR ?? null,
      };

      // Keep the source application and an existing Recruitment copy aligned.
      await saveSharedRecruitmentUpdate(advisorId, payload);

      onSaved?.();
      onClose?.();
    } catch (err) {
      // console.error("Error updating notes:", err);
      setError("Sorry, we couldn't save this update. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const selectedPermissions = resolveRecruitmentPermissions(null, updateData);
  const permissionOptions = Array.from(new Set([
    ...Object.values(AdviserStatus),
    ...selectedPermissions,
  ])).map((value) => ({ value, label: value }));

  const changeField = (field) => (event) => {
    setUpdateData({ ...updateData, [field]: event.target.value });
  };

  return (
    <Popup
      open={isOpen}
      title="Update recruitment"
      description={
        updateData?.advisor ? (
          <span className="FP_Content_Semibold text-black">{updateData.advisor}</span>
        ) : (
          "Keep the applicant's details and latest notes up to date."
        )
      }
      className="recruitment-update-popup"
      confirmLabel={saving ? "Saving..." : "Save update"}
      cancelLabel="Cancel"
      busy={saving}
      onConfirm={handleSubmit}
      onCancel={onClose}
    >
      <form
        id="recruitment-update-form"
        onSubmit={handleSubmit}
        className="recruitment-update-popup__form"
      >
        <div className="recruitment-update-popup__grid">
          <NewInputField
            id="recruitment-applicant-name"
            label="Applicant name"
            value={updateData?.applicantName || ""}
            onChange={changeField("applicantName")}
            placeholder="Enter applicant name"
            disabled={saving}
          />
          <NewInputField
            id="recruitment-source"
            label="Source"
            value={updateData?.source || ""}
            onChange={changeField("source")}
            placeholder="Website or referral"
            disabled={saving}
          />

          <NewInputField
            id="recruitment-appointment"
            label="Anticipated appointment date"
            value={updateData?.appointmentData || ""}
            onChange={changeField("appointmentData")}
            placeholder="Enter anticipated date or timeframe"
            disabled={saving}
          />
          <CheckboxMultiSelect
            label="Permission"
            value={selectedPermissions}
            options={permissionOptions}
            onChange={(permissions) =>
              setUpdateData({
                ...updateData,
                status: permissions,
                permission: permissions.join(", "),
              })
            }
            placeholder="Select permissions"
            helperText="Select all relevant permissions"
            disabled={saving}
          />
        </div>
        <div className="recruitment-update-popup__grid">
        <fieldset className="recruitment-update-popup__ar" disabled={saving}>
          <RadioGroup
            label="Joining as"
            name="recruitment-isAR"
            options={[
              { value: true, label: "AR" },
              { value: false, label: "TS" },
            ]}
            value={updateData?.isAR ?? null}
            onChange={(value) => setUpdateData({ ...updateData, isAR: value })}
            columns={2}
          />
        </fieldset>
          <NewInputField
            id="recruitment-ar-ts-name"
            label={updateData?.isAR === true ? "AR Name" : updateData?.isAR === false ? "TS Name" : "AR/TS Name"}
            value={updateData?.arTsName || ""}
            onChange={changeField("arTsName")}
            placeholder={updateData?.isAR === true ? "Enter AR name" : updateData?.isAR === false ? "Enter TS name" : "Enter AR/TS name"}
            disabled={saving}
          />
        </div>

          <NewDateField
            id="recruitment-last-contacted"
            label="Last contacted"
            value={formatDateValue(updateData?.lastContacted)}
            onChange={changeField("lastContacted")}
            disabled={saving}
          />
        <NewTextArea
          id="recruitment-notes"
          label="Notes"
          value={updateData?.description || ""}
          onChange={changeField("description")}
          placeholder="Add the latest recruitment update..."
          maxLength={null}
          disabled={saving}
        />
        {error && (
          <div className="FP_Content text-error" role="alert">
            {error}
          </div>
        )}
      </form>
    </Popup>
  );
};

export default UpdateRecruitmentStatus;
